// Components
import { Input, Form, Checkbox } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import Label from "components/atoms/Label/Label";
import ErrorMessage from "components/molecules/ErrorMessage/ErrorMessage";

// Styles
import styles from "components/organisms/NewUser/newUserDetails.module.css";

// Constants
import { NewUser as Constants } from "constants/global";

// Types
import { FieldErrors } from "containers/NewUser/useNewUser";

type NewUserDetailsProps = {
	adminData: {
		firstname: string;
		lastname: string;
		username: string;
		email: string;
	};
	is2FAChecked: boolean;
	mode?: string;
	initialValues?: {
		first_name: string;
		last_name: string;
		username: string;
		email: string;
		mfa_enabled: boolean;
		groups: string[];
	};
	readOnly?: boolean;
	disabled?: boolean;
	fieldErrors: FieldErrors;
	disable2FACheckbox?: string;
	validateFields: (arg: boolean) => void;
	handleAdminDataChange: (value: { id: string; value: string }) => void;
	handleEnable2FA: (value: boolean) => void;
};

type MFACheckboxInputTypes = {
	handleEnable2FA: (value: boolean) => void;
	is2FAChecked: boolean;
	readOnly?: boolean;
	disable2FACheckbox?: string;
	initialValue?: boolean;
};

type CheckboxProps = {
	prefixCls: string;
	className: string;
	checked?: boolean;
	onChange?: (e: CheckboxChangeEvent) => void;
};

const MFACheckboxInput = ({ disable2FACheckbox = "", readOnly, is2FAChecked, initialValue, handleEnable2FA }: MFACheckboxInputTypes) => {
	const props: CheckboxProps = {
		prefixCls: styles?.enable2FA_button_prefix,
		className: styles?.enable2FA_button,
		checked: readOnly ? initialValue : is2FAChecked
	};

	if (!readOnly || disable2FACheckbox === Constants.DISABLE_CHECKBOX) {
		props.onChange = (e) => handleEnable2FA(e.target?.checked);
	}

	return (
		<Form.Item name="enable2FA">
			<Checkbox {...props} disabled={disable2FACheckbox === Constants.DISABLE_CHECKBOX}>
				{Constants.ENABLE_2FA_LABEL}
			</Checkbox>
		</Form.Item>
	);
};

type ErrorMessageWrapperTypes = {
	fieldError?: boolean;
	message: string;
};

const ErrorMessageWrapper = ({ fieldError, message }: ErrorMessageWrapperTypes) => {
	return fieldError ? <ErrorMessage classes={styles?.err_message} message={message} /> : <></>;
};

const NewUserDetails = ({
	fieldErrors,
	initialValues,
	adminData,
	is2FAChecked,
	readOnly,
	disabled,
	disable2FACheckbox,
	validateFields,
	handleEnable2FA,
	handleAdminDataChange
}: NewUserDetailsProps) => {
	const handleChange = (e: { target: { id: any; value: any } }) =>
		handleAdminDataChange({
			id: e?.target?.id,
			value: e?.target?.value
		});

	const firstNameClasses = fieldErrors?.firstname ? styles?.err : styles?.filled;
	const lastNameClasses = fieldErrors?.lastname ? styles?.err : styles?.filled;
	const usernameClasses = fieldErrors?.username ? styles?.err : styles?.filled;
	const emailClasses = fieldErrors?.email ? styles?.err : styles?.filled;

	return (
		<Form className={styles?.form} requiredMark={false} layout="vertical">
			<div className={styles?.field_wrapper}>
				<Form.Item
					extra={<ErrorMessageWrapper fieldError={fieldErrors?.firstname} message={Constants.INVALID_FIRSTNAME} />}
					name={Constants.FIELD_FNAME}
					initialValue={initialValues?.first_name}
					label={<Label label={Constants.FIRST_NAME_LABEL} classes={{ label: styles?.step_input_label }} />}
				>
					<Input
						data-testid={Constants.FIELD_FNAME}
						readOnly={readOnly}
						placeholder={Constants.FIRST_NAME_PLACEHOLDER}
						className={`${styles?.step_input} ${adminData?.firstname ? firstNameClasses : ""}`}
						value={adminData?.firstname}
						onChange={handleChange}
						onBlur={() => validateFields(false)}
					/>
				</Form.Item>
				<Form.Item
					extra={<ErrorMessageWrapper fieldError={fieldErrors?.lastname} message={Constants.INVALID_LASTNAME} />}
					name={Constants.FIELD_LNAME}
					initialValue={initialValues?.last_name}
					label={<Label label={Constants.LAST_NAME_LABEL} classes={{ label: styles?.step_input_label }} />}
				>
					<Input
						data-testid={Constants.FIELD_LNAME}
						readOnly={readOnly}
						placeholder={Constants.LAST_NAME_PLACEHOLDER}
						className={`${styles?.step_input} ${adminData?.lastname ? lastNameClasses : ""}`}
						value={adminData?.lastname}
						onChange={handleChange}
						onBlur={() => validateFields(false)}
					/>
				</Form.Item>
			</div>
			<div className={styles?.field_wrapper}>
				<Form.Item
					extra={<ErrorMessageWrapper fieldError={fieldErrors?.username} message={Constants.INVALID_USERNAME} />}
					name={Constants.FIELD_USERNAME}
					initialValue={initialValues?.username}
					label={<Label label={Constants.USERNAME_LABEL} classes={{ label: styles?.step_input_label }} requiredMark />}
				>
					<Input
						data-testid={Constants.FIELD_USERNAME}
						disabled={disabled}
						placeholder={Constants.USERNAME_PLACEHOLDER}
						className={`${styles?.step_input} ${adminData?.username ? usernameClasses : ""}`}
						value={adminData?.username}
						onChange={handleChange}
						onBlur={() => validateFields(false)}
					/>
				</Form.Item>
				<Form.Item
					extra={<ErrorMessageWrapper fieldError={fieldErrors?.email} message={Constants.INVALID_EMAIL} />}
					name={Constants.FIELD_EMAIL}
					initialValue={initialValues?.email}
					label={<Label label={Constants.EMAIL_LABEL} classes={{ label: styles?.step_input_label }} requiredMark />}
				>
					<Input
						data-testid={Constants.FIELD_EMAIL}
						readOnly={readOnly}
						placeholder={Constants.EMAIL_PLACEHOLDER}
						className={`${styles?.step_input} ${adminData?.email ? emailClasses : ""}`}
						value={adminData?.email}
						onChange={handleChange}
						onBlur={() => validateFields(false)}
					/>
				</Form.Item>
			</div>
			<div className={styles?.enable2FA_wrapper}>
				<MFACheckboxInput
					is2FAChecked={is2FAChecked}
					disable2FACheckbox={disable2FACheckbox}
					initialValue={initialValues?.mfa_enabled}
					readOnly={readOnly}
					handleEnable2FA={handleEnable2FA}
				/>
			</div>
		</Form>
	);
};

export default NewUserDetails;
